import { useCallback, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import styles from './EndSessionGesture.module.css';

export interface EndSessionGestureProps {
  /** Acionado quando o gesto de encerramento é completo — 5 toques dentro
   * de uma janela de 3s (Design > aprovado 2026-07-30, LOCAL-003). */
  onTrigger: () => void;
}

/** Quantidade de toques exigida dentro da janela (Design > gesto aprovado). */
const REQUIRED_TAPS = 5;

/** Duração da janela deslizante, em ms (Design > gesto aprovado). */
const WINDOW_MS = 3_000;

/**
 * Hit-target invisível (44x44px — WCAG 2.5.5, mesmo mínimo de
 * `OnlineStatusIndicator`), fixado no canto **inferior-esquerdo** da tela
 * (o canto superior-direito já é ocupado por `OnlineStatusIndicator`) —
 * encerra a sessão de dispositivo com 5 toques em até 3s (LOCAL-003, gesto
 * aprovado pelo Designer em 2026-07-30).
 *
 * Deliberadamente sem rótulo/ícone visível nem exposição a leitor de tela
 * (`aria-hidden`, fora da ordem de tabulação): o gesto é conhecido só pelo
 * staff da agência, nunca revelado ao participante (Design > motivação do
 * gesto discreto).
 *
 * A janela desliza a cada toque (mantém só os toques dos últimos 3s): 5
 * toques mais lentos que isso não acionam o encerramento; o contador reflui
 * naturalmente enquanto a janela não fecha.
 */
export function EndSessionGesture({ onTrigger }: EndSessionGestureProps): ReactElement {
  const tapTimestampsRef = useRef<number[]>([]);
  // Só usado para reiniciar a animação de pulso a cada toque válido (Design
  // > sugestão não-bloqueante) — nenhuma lógica de negócio depende dele.
  const [pulseKey, setPulseKey] = useState(0);

  const handleTap = useCallback((): void => {
    const now = Date.now();
    const recentTaps = [...tapTimestampsRef.current, now].filter((tap) => now - tap <= WINDOW_MS);

    if (recentTaps.length >= REQUIRED_TAPS) {
      tapTimestampsRef.current = [];
      onTrigger();
      return;
    }

    tapTimestampsRef.current = recentTaps;
    setPulseKey((key) => key + 1);
  }, [onTrigger]);

  return (
    <button
      type="button"
      className={styles.hitTarget}
      onClick={handleTap}
      aria-hidden="true"
      tabIndex={-1}
      data-testid="end-session-gesture"
    >
      {/* `pulseKey > 0` evita disparar a animação já na montagem inicial —
          só reaparece a cada toque válido, remontada via `key`. */}
      {pulseKey > 0 && <span key={pulseKey} className={styles.pulse} aria-hidden="true" />}
    </button>
  );
}
